import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../app/store";
import { useAppSelector, useAppDispatch } from "../../app/hooks";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import { closeDialog, openDialog } from "../../components/dialog/dialogSlice";
import IconButton from "../../components/common/IconButton";
import { FiEdit } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import AppDialog from "../../components/dialog/Dialog";
import { DialogCloseButton } from "../../components/dialog/Dialog";
import { Switch } from "@progress/kendo-react-inputs";
import AlertBox from "../../components/common/AlertBox";
import { Tooltip } from "@progress/kendo-react-tooltip";
import {
  getAllServiceLocation,
  deleteServiceLocation,
  activeInactiveServiceLocation,
} from "./services/serviceLocation.services";
import { clearServiceLocationID, setServiceLocationID } from "./serviceLocationSlice";
import { checkAcessRights } from "../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../_contstants/common";

const ServiceLocation: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isAddAccess = checkAcessRights(location.pathname, INSERT_ACCESS);

  useEffect(() => {
    dispatch(getAllServiceLocation({ CityName: "" }));
    dispatch(closeDialog());
  }, []);

  const handleCreate = () => {
    navigate("/servicelocation/create");
  };

  return (
    <>
      <DeleteServiceLocationDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0px 20px",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            Service Location List
          </Typography.h4>
          <div>
            {isAddAccess && (
              <Button
                onClick={handleCreate}
                fillMode={"solid"}
                themeColor={"primary"}
              >
                Add New
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <ServiceLocationGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const ServiceLocationGridComponent: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isEditAccess = checkAcessRights(location.pathname, EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights(location.pathname, DELETE_ACCESS);

  const list = useAppSelector((state: RootState) => state.serviceLocation.ServiceLocationList);
  const loading = useAppSelector((state: RootState) => state.serviceLocation.loading);

  const [filter, setFilter] = React.useState<CompositeFilterDescriptor | undefined>(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<number | string | undefined>();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take = targetEvent.value === "All" ? list.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (ID: number) => {
      navigate("/servicelocation/edit", { state: { ServiceLocationID: ID } });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteServiceLocation"));
      dispatch(setServiceLocationID(ID));
    };
    return (
      <td className={`action-td ${props.className}`} style={{ ...props.style, right: 0 }}>
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem.ID)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            className="color-red"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 3 || props.index === 4 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <td {...props.tdProps} colSpan={1} className={"table-cell"}>
      {props.children}
    </td>
  );

  const MyActiveInactiveCommandCell = (props: GridCellProps) => {
    const [isActive, setIsActive] = useState<boolean>(props.dataItem.IsActive);

    const handleActiveInactive = () => {
      const newValue = !isActive;
      setIsActive(newValue);
      dispatch(activeInactiveServiceLocation({ ID: props.dataItem.ID, IsActive: newValue })).then(() => {
        dispatch(getAllServiceLocation({ CityName: "" }));
      });
    };

    return (
      <td style={{ textAlign: "center", ...props.style, right: 110 }} className={props.className}>
        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <div title={`Set ${isActive ? "Inactive" : "Active"}.`}>
            <Switch
              disabled={!isEditAccess}
              checked={isActive}
              onChange={handleActiveInactive}
              size="small"
            />
          </div>
        </Tooltip>
      </td>
    );
  };

  const gridRef = useRef<any>(null);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      {list && list.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={filter ? filterBy(list, filter).slice(page.skip, page.take + page.skip) : list.slice(page.skip, page.take + page.skip)}
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={list.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="CityName"
            title="City Name"
            width={200}
            cells={{
              data: (props: any) => (
                <td>
                  <div
                    className={isEditAccess ? "hoverableName" : "normalName"}
                    onClick={() =>
                      isEditAccess &&
                      navigate("/servicelocation/edit", {
                        state: { ServiceLocationID: props.dataItem?.ID },
                      })
                    }
                  >
                    {props.dataItem?.CityName}
                  </div>
                </td>
              ),
            }}
          />
          <Column
            field="Destinations"
            title="Destinations"
            width={250}
            cells={{
              data: (props: any) => (
                <td className="table-cell">{props.dataItem?.Destinations}</td>
              ),
            }}
          />
          <Column
            field="ID"
            title="Status"
            width={"90px"}
            filterable={false}
            locked={true}
            cells={{
              data: MyActiveInactiveCommandCell,
            }}
          />
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              width={"110px"}
              filterable={false}
              locked={true}
              cells={{
                data: MyEditCommandCell,
              }}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteServiceLocationDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const dialogName = useAppSelector((state: RootState) => state.dialog.dialogName);
  const id = useAppSelector((state: RootState) => state.serviceLocation.ServiceLocationID);

  const handleDelete = async () => {
    if (id) {
      await dispatch(deleteServiceLocation(id));
      dispatch(getAllServiceLocation({ CityName: "" }));
      dispatch(closeDialog());
      dispatch(clearServiceLocationID());
    }
  };

  return (
    <>
      {dialogName === "deleteServiceLocation" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Service Location?"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>Are you sure you want to delete this record?</Typography.p>
              </GridLayoutItem>
              <GridLayoutItem style={{ display: "flex", justifyContent: "end" }}>
                <DialogCloseButton themeColor="error" />
                <Button fillMode={"solid"} themeColor={"error"} onClick={handleDelete} type="button">
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default ServiceLocation;
